// TreeNav — domain-neutral filterable tree navigation.
// Props:
//   nodes: TreeNode[]   — [{id, code, label, counts?, children?}]
//   selected: string | null  — currently selected node id (null = root / no selection)
//   onSelect: (id: string | null) => void
//   onDrillDown?: (id: string) => void  — double-click on a row drills into children (DataGrid)
//   filterPlaceholder?: string
//   filterAside?: node  — rendered on the filter's own row, right of the input (the
//                     "Standard" box, Alex 2026-10-15: "beside the tree 'Filter'").
//   codeWidths?: number[]   — min-width (px) for the code column at each depth [0,1,2,…]; defaults [48,56,40]
//   title?: string  — when provided, renders a full-width header at the very top that selects root
//                     on single-click (onSelect(null)) and highlights with the same active style as a
//                     selected node when selected === null. Replaces the former rootLabel prop.
//   sideIcons?: boolean  — NIGHT_SHIFT.md item 7b/task 3 (2026-07-21). Onshape-only
//                     opt-in: shows CATALOG (▣) / PRODUCT (⊞) on top-level (L0) nodes
//                     that carry `node._side` ('c'|'p', from tree-model.js's composite
//                     tree). Catalog/Products pass nothing (single-source trees never
//                     set `_side`), so this is a no-op for them either way. Default false.
//
// TreeNode: { id: string, code: string, label: string, counts?: CountMap, children?: TreeNode[] }
// CountMap: { draft?, in_progress?, validated_db?, validated_synced?, deprecated?, total? }
//
// Interaction (brief §1):
//   single-click anywhere on row = SELECT (load grid); onSelect(id)
//   double-click anywhere on row = EXPAND/COLLAPSE; chevron also toggles
//   chevron click = toggle expand/collapse (stopPropagation so single-click doesn't also fire)
//
// Status glyph (brief §2):
//   ∅  = zero non-deprecated items ("needs content")
//   ✎  = any draft or in_progress ("work left")
//   ✓  = all validated, none draft/in_progress ("done") — OR blank when no counts passed
//   Rendered as a single soft rectangular muted chip; shape carries meaning, NOT color.
//   Counts → tooltip only.
//
// Styling: all colors/spacing via --tg-* / --th-* tokens in public/theme.css. No raw hex.

import { useState, useMemo, useRef, useEffect } from 'react';
import { EMPTY, EDIT, OK, WARN, DEPRECATED, LINK, CATALOG, PRODUCT } from '../icons.js';
import Chevron from './Chevron.jsx';

// Compute the rollup glyph from a counts object.
// Excludes deprecated. Returns EMPTY | EDIT | OK | null (null = no counts provided).
export function statusGlyph(counts) {
  if (!counts) return null;
  const draft     = counts.draft || 0;
  const ip        = counts.in_progress || 0;
  const validated = (counts.validated_db || 0) + (counts.validated_synced || 0);
  const total     = draft + ip + validated; // non-deprecated total
  if (total === 0) return EMPTY;
  if (draft > 0 || ip > 0) return EDIT;
  return OK;
}

// Tooltip text for the glyph (raw counts for accessibility).
function glyphTooltip(counts) {
  if (!counts) return '';
  const parts = [];
  if (counts.draft)        parts.push(`${counts.draft} draft`);
  if (counts.in_progress)  parts.push(`${counts.in_progress} in progress`);
  const v = (counts.validated_db || 0) + (counts.validated_synced || 0);
  if (v)                   parts.push(`${v} validated`);
  if (counts.deprecated)   parts.push(`${counts.deprecated} deprecated`);
  return parts.join(' · ');
}

// Muted rectangular status glyph badge. Shape = meaning; no color signal.
export function StatusGlyph({ counts }) {
  const glyph = statusGlyph(counts);
  if (!glyph) return null;
  return (
    <span
      title={glyphTooltip(counts)}
      style={{
        background: 'var(--tg-glyph-bg)',
        color: 'var(--tg-glyph-color)',
        border: 'var(--tg-glyph-border)',
        borderRadius: 'var(--tg-glyph-radius)',
        padding: 'var(--tg-glyph-pad)',
        fontSize: 'var(--fs-micro)',
        fontWeight: 700,
        marginLeft: 'auto',
        flexShrink: 0,
        userSelect: 'none',
        lineHeight: 1.4,
        display: 'inline-block',
      }}
    >
      {glyph}
    </span>
  );
}

// Per-status QUANTITY chips for grid columns (DECISION, Alex 2026-07-02: level grids
// show counts, not the bare glyph — do not revert to glyph-only unless Alex asks).
// ⚠ N = formula errors · ✎ N = draft + in progress · ✓ N = validated · ✗ N = deprecated
// · ↗ N = Onshape-linked (2026-07-04) · ∅ = empty. Rendered as FIXED-WIDTH SLOTS in
// a stable order so the icons line up vertically across rows (Alex, 2026-07-02); a
// zero slot stays blank but keeps its width. Color-blind safe: icon + number carry
// the meaning; muted chip style.
export function StatusCounts({ counts }) {
  if (!counts) return null;
  const errors     = counts.errors || 0;
  const draft      = (counts.draft || 0) + (counts.in_progress || 0);
  const validated  = (counts.validated_db || 0) + (counts.validated_synced || 0);
  const deprecated = counts.deprecated || 0;
  const linked     = counts.linked || 0;
  // linked is NOT part of the "is this node empty" check — it's an orthogonal
  // Onshape-link tally, not a status; an all-linked, zero-status node would be
  // a contradiction that can't occur (a line must have a status).
  const empty = draft + validated + deprecated === 0;
  const slots = [
    { icon: WARN, n: errors,     word: `${errors} formula error(s)` },
    { icon: EDIT, n: draft,      word: `${draft} draft / in progress` },
    { icon: OK, n: validated,  word: `${validated} validated` },
    { icon: DEPRECATED, n: deprecated, word: `${deprecated} deprecated` },
    { icon: LINK, n: linked,     word: `${linked} linked to Onshape` },
  ];
  const slotStyle = {
    minWidth: 42,
    textAlign: 'left',
    fontSize: 'var(--fs-micro)',
    fontWeight: 700,
    userSelect: 'none',
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  };
  const chipStyle = {
    background: 'var(--tg-glyph-bg)',
    color: 'var(--tg-glyph-color)',
    border: 'var(--tg-glyph-border)',
    borderRadius: 'var(--tg-glyph-radius)',
    padding: 'var(--tg-glyph-pad)',
    display: 'inline-block',
  };
  if (empty) {
    return (
      <span style={{ display: 'inline-flex', gap: 3, alignItems: 'center' }}>
        <span style={slotStyle}><span title="empty" style={chipStyle}>{EMPTY}</span></span>
      </span>
    );
  }
  return (
    <span style={{ display: 'inline-flex', gap: 3, alignItems: 'center' }}>
      {slots.map(s => (
        <span key={s.icon} style={slotStyle}>
          {s.n > 0 ? <span title={s.word} style={chipStyle}>{s.icon} {s.n}</span> : null}
        </span>
      ))}
    </span>
  );
}

function nodeMatches(node, filter) {
  const f = filter.toLowerCase();
  if (node.code.toLowerCase().includes(f) || node.label.toLowerCase().includes(f)) return true;
  return (node.children || []).some(c => nodeMatches(c, f));
}

// subtreeHasId(node, id): is `id` this node or any descendant? Drives auto-open
// below. Cheap — stops at the first hit, and only walks while a selection exists.
function subtreeHasId(node, id) {
  if (id == null || !node) return false;
  if (node.id === id) return true;
  return (node.children || []).some(child => subtreeHasId(child, id));
}

const DEFAULT_CODE_WIDTHS = [48, 56, 40];

// SideIcon — NIGHT_SHIFT.md item 7b/task 3 (2026-07-21): the Onshape screen's
// composite tree merges catalog + product nodes (`node._side`, from
// tree-model.js's namespacing) with no visual cue which side a top-level node
// belongs to. Opt-in via `sideIcons` (a prop the Onshape screen passes, per
// Alex's ask) rather than reading `node._side` unconditionally — Catalog and
// Products never set `_side` on their (single-source, passthrough) trees, so
// this is a no-op for them either way, but an explicit flag keeps that a
// documented choice instead of an implicit data-shape assumption. Renders on
// L0 (top-level) nodes only — one marker per branch is enough to read the
// side at a glance; every descendant already carries the same `_side`.
function SideIcon({ side }) {
  if (side !== 'c' && side !== 'p') return null;
  const glyph = side === 'c' ? CATALOG : PRODUCT;
  const label = side === 'c' ? 'Catalog' : 'Product';
  return (
    <span
      title={label}
      aria-label={label}
      style={{
        fontSize: 'var(--fs-small)',
        color: 'var(--tg-code-color-other)',
        flexShrink: 0,
        width: 14,
        textAlign: 'center',
        userSelect: 'none',
      }}
    >
      {glyph}
    </span>
  );
}

function NavNode({ node, depth, selected, onSelect, filter, codeWidths, sideIcons }) {
  const hasChildren = node.children && node.children.length > 0;
  const isActive = selected === node.id;
  // Trees start collapsed (brief §2) — EXCEPT a branch that contains the current
  // selection, which opens so the selected node is actually reachable. Fixes two
  // things at once: a `?view=&node=` deep link (Alex 2026-07-16 — "open the tree
  // to the correct location"), and the pre-existing reload case, where a node
  // restored from localStorage was selected but buried in a collapsed tree.
  // Derived from the `selected` prop the node already has — no new API.
  const [open, setOpen] = useState(() => subtreeHasId(node, selected));
  const [hovered, setHovered] = useState(false);

  // Re-open when the selection MOVES into this branch (deep link on an already
  // mounted tree). Only on an actual change, so a manual collapse still sticks.
  const prevSelected = useRef(selected);
  useEffect(() => {
    if (selected === prevSelected.current) return;
    prevSelected.current = selected;
    if (subtreeHasId(node, selected)) setOpen(true);
  }, [selected, node]);

  const forceOpen = !!filter;
  const shouldOpen = forceOpen || open;

  const visibleChildren = filter
    ? (node.children || []).filter(c => nodeMatches(c, filter))
    : (node.children || []);

  const padLeft = 8 + depth * 14;
  const isL0 = depth === 0;
  const isL1 = depth === 1;
  const resolvedWidths = codeWidths || DEFAULT_CODE_WIDTHS;
  const codeWidth = resolvedWidths[depth] ?? resolvedWidths[resolvedWidths.length - 1] ?? 40;

  const background = isActive
    ? 'var(--tg-node-active-bg)'
    : (hovered ? 'var(--th-row-hover)' : 'transparent');

  const padY = isL0 ? 'var(--tg-node-l0-pad-y)' : (isL1 ? 'var(--tg-node-l1-pad-y)' : 'var(--tg-node-l2-pad-y)');

  return (
    <div>
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          padding: `${padY} var(--tg-node-pad-x) ${padY} ${padLeft}px`,
          cursor: 'pointer',
          background,
          borderLeft: isActive ? 'var(--tg-node-active-border)' : 'var(--tg-node-inactive-border)',
          borderBottom: isL0 ? 'var(--tg-node-l0-border-btm)' : 'none',
        }}
        onClick={() => onSelect(node.id)}
        onDoubleClick={() => setOpen(o => !o)}
      >
        {hasChildren ? (
          <span
            onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
            style={{
              fontSize: 'var(--fs-body)',
              color: isL0 ? 'var(--tg-code-color-l0)' : 'var(--tg-code-color-other)',
              flexShrink: 0, width: 14, userSelect: 'none',
              fontWeight: isL0 ? 700 : 400,
            }}
          >
            <Chevron open={shouldOpen} style={{ fontSize: 'inherit', width: 'auto' }} />
          </span>
        ) : (
          <span style={{ width: 14, flexShrink: 0 }} />
        )}
        {sideIcons && isL0 && <SideIcon side={node._side} />}
        <span style={{
          fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-body)',
          fontWeight: isL0 ? 700 : 400,
          color: isL0 ? 'var(--tg-code-color-l0)' : 'var(--tg-code-color-other)',
          flexShrink: 0,
          minWidth: codeWidth, display: 'inline-block',
        }}>
          {node.code}
        </span>
        <span style={{
          fontSize: 'var(--fs-body)', fontWeight: isL0 ? 600 : 400,
          color: 'var(--tg-label-color)',
          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {node.label}
        </span>
        <StatusGlyph counts={node.counts} />
      </div>
      {shouldOpen && visibleChildren.map(child => (
        <NavNode
          key={child.id}
          node={child}
          depth={depth + 1}
          selected={selected}
          onSelect={onSelect}
          filter={filter}
          codeWidths={codeWidths}
          sideIcons={sideIcons}
        />
      ))}
    </div>
  );
}

export default function TreeNav({ nodes, selected, onSelect, filterPlaceholder = 'Filter…', filterAside = null, codeWidths, title, sideIcons = false, loading, emptyMessage = 'Nothing here yet.' }) {
  const [filter, setFilter] = useState('');
  const [titleHovered, setTitleHovered] = useState(false);

  const visible = useMemo(
    () => filter ? (nodes || []).filter(n => nodeMatches(n, filter)) : (nodes || []),
    [nodes, filter]
  );

  const titleActive = title != null && selected === null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {title != null && (
        <div
          onMouseEnter={() => setTitleHovered(true)}
          onMouseLeave={() => setTitleHovered(false)}
          onClick={() => onSelect(null)}
          style={{
            padding: 'var(--tg-node-pad-x)',
            cursor: 'pointer',
            background: titleActive ? 'var(--tg-node-active-bg)' : (titleHovered ? 'var(--th-row-hover)' : 'var(--tg-node-l0-bg)'),
            borderLeft: titleActive ? 'var(--tg-node-active-border)' : 'var(--tg-node-inactive-border)',
            borderBottom: 'var(--tg-filter-border)',
            flexShrink: 0,
            fontSize: 'var(--fs-body)',
            fontWeight: 700,
            color: 'var(--th-navy)',
            userSelect: 'none',
          }}
        >
          {title}
        </div>
      )}
      <div style={{ padding: 'var(--tg-filter-pad)', borderBottom: 'var(--tg-filter-border)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
        <input
          type="search"
          placeholder={filterPlaceholder}
          value={filter}
          onChange={e => setFilter(e.target.value)}
          style={{
            // Matches the TREE NODES (--fs-body), not the old --fs-small
            // (Alex, 2026-07-23: "same font setup as the tree. It is too
            // small"). An input does NOT inherit the page font family, so set
            // it explicitly — otherwise it falls back to the browser's default
            // form font and reads as a different typeface even at the right size.
            width: '100%', fontSize: 'var(--fs-body)', fontFamily: 'inherit',
            padding: '4px 7px',
            border: 'var(--tg-filter-border)', borderRadius: 4,
            outline: 'none', boxSizing: 'border-box', flex: 1, minWidth: 0,
          }}
        />
        {filterAside}
      </div>
      <div style={{ overflowY: 'auto', flex: 1, paddingBottom: 12 }}>
        {/* ⚠ EMPTY IS NOT THE SAME AS NOT-LOADED-YET, and this said "Loading…" for both.
            Alex, 2026-08-16: "Internal seems very long or is not loading" — his new project
            had ZERO functions, the API answered in 2ms, and the tree span forever. A
            spinner that never resolves is worse than an empty state: it says the app is
            working when it has already finished.
            `loading` is OPT-IN (undefined keeps the old behaviour) because five screens
            mount this and only the caller knows whether its fetch has returned. */}
        {(!nodes || nodes.length === 0) && (
          <div style={{ padding: 16, color: 'var(--tg-code-color-other)', fontSize: 'var(--fs-small)' }}>
            {loading === false ? emptyMessage : 'Loading…'}
          </div>
        )}
        {filter && visible.length === 0 && (
          <div style={{ padding: 12, color: 'var(--tg-code-color-other)', fontSize: 'var(--fs-small)' }}>No matches.</div>
        )}
        {visible.map(node => (
          <NavNode
            key={node.id}
            node={node}
            depth={0}
            selected={selected}
            onSelect={onSelect}
            filter={filter}
            codeWidths={codeWidths}
            sideIcons={sideIcons}
          />
        ))}
      </div>
    </div>
  );
}
